/** Every version of an evaluator, newest first, each with the mapping it held. */

import { Box } from "@/components/ui/box";
import { Pill } from "@/components/ui/pill";
import { QueryState } from "@/components/ui/states";
import { formatDate } from "@/lib/format";
import { useEvaluatorVersions } from "./hooks";
import type { Evaluator } from "./types";

export function EvaluatorVersions({ evaluator }: { evaluator: Evaluator }) {
  const versions = useEvaluatorVersions(evaluator.id);
  const rows = [...(versions.data ?? [])].sort((a, b) => b.version - a.version);
  const latest = rows[0]?.version ?? 1;
  return (
    <Box className="flex flex-col gap-2 p-5" label="Versions">
      <h3 className="text-[13px] font-semibold">Versions</h3>
      <QueryState query={versions} what="versions">
        <ol
          aria-label="Versions"
          className="flex flex-col divide-y divide-line-soft rounded-lg border border-line-soft"
        >
          {rows.map((v) => (
            <li key={v.id} className="flex flex-col gap-1 px-3 py-2.5">
              <span className="flex items-center gap-2">
                <span className="font-mono text-xs font-semibold">v{v.version}</span>
                <span className="text-[13px]">{v.name}</span>
                {v.version === latest && <Pill tone="run">Current</Pill>}
                <span className="grow" />
                {v.created_at && <span className="text-xs text-muted">{formatDate(v.created_at)}</span>}
              </span>
              {Object.keys(v.default_mapping ?? {}).length > 0 && (
                <span className="font-mono text-[11px] text-faint">
                  {Object.entries(v.default_mapping)
                    .map(([name, path]) => `{{${name}}} → ${path ?? "not mapped"}`)
                    .join(" · ")}
                </span>
              )}
            </li>
          ))}
        </ol>
      </QueryState>
    </Box>
  );
}
